import {
  useCallback,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  selectSelectedConsultation,
  selectPatientConsultations,
  selectDoctorConsultations,
  selectConsultationLoading,
  selectConsultationError,
  selectConsultationSuccess,
} from "../redux/selectors/consultationSelectors";

import {
  fetchConsultationById,
  fetchConsultationsByPatientId,
  fetchConsultationsByDoctorId,
  createConsultation,
  updateConsultation,
  updateConsultationPayment,
  deleteConsultation,
} from "../redux/thunks/consultationThunks";

/* =========================================================
   HOOK
========================================================= */

const useConsultations =
  () => {
    const dispatch =
      useDispatch();

    /* =====================================================
       STATE
    ===================================================== */

    const selectedConsultation =
      useSelector(
        selectSelectedConsultation
      );

    const patientConsultations =
      useSelector(
        selectPatientConsultations
      );

    const doctorConsultations =
      useSelector(
        selectDoctorConsultations
      );

    const isLoading =
      useSelector(
        selectConsultationLoading
      );

    const error =
      useSelector(
        selectConsultationError
      );

    const success =
      useSelector(
        selectConsultationSuccess
      );

    /* =====================================================
       GET BY ID
    ===================================================== */

    const getById =
      useCallback(
        (id) =>
          dispatch(
            fetchConsultationById(
              id
            )
          ),
        [
          dispatch,
        ]
      );

    /* =====================================================
       GET BY PATIENT

       Utilisation possible :

       getByPatientId(userId)

       ou

       getByPatientId(
         userId,
         doctorId
       )
    ===================================================== */

    const getByPatientId =
      useCallback(
        (
          userId,
          doctorId
        ) =>
          dispatch(
            fetchConsultationsByPatientId(
              {
                userId,
                doctorId,
              }
            )
          ),
        [
          dispatch,
        ]
      );

    /* =====================================================
       GET BY DOCTOR
    ===================================================== */

    const getByDoctorId =
      useCallback(
        (
          doctorId
        ) =>
          dispatch(
            fetchConsultationsByDoctorId(
              doctorId
            )
          ),
        [
          dispatch,
        ]
      );

    /* =====================================================
       CREATE

       IMPORTANT:

       const result =
         await create(dto)
           .unwrap();

       fonctionne.
    ===================================================== */

    const create =
      useCallback(
        (dto) =>
          dispatch(
            createConsultation(
              dto
            )
          ),
        [
          dispatch,
        ]
      );

    /* =====================================================
       UPDATE
    ===================================================== */

    const update =
      useCallback(
        (
          id,
          dto
        ) =>
          dispatch(
            updateConsultation(
              {
                consultationId:
                  id,

                consultationData:
                  dto,
              }
            )
          ),
        [
          dispatch,
        ]
      );

    const updatePayment =
      useCallback(
        (id, dto) =>
          dispatch(
            updateConsultationPayment({
              consultationId: id,
              consultationData: dto,
            })
          ),
        [dispatch]
      );

    /* =====================================================
       DELETE
    ===================================================== */

    const remove =
      useCallback(
        (
          id
        ) =>
          dispatch(
            deleteConsultation(
              id
            )
          ),
        [
          dispatch,
        ]
      );

    /* =====================================================
       RETURN
    ===================================================== */

    return {
      selectedConsultation,

      patientConsultations,

      doctorConsultations,

      isLoading,

      error,

      success,

      getById,

      getByPatientId,

      getByDoctorId,

      create,

      update,

      updatePayment,

      remove,
    };
  };

export default useConsultations;